import React from 'react';
import {
  DocumentTextIcon,
  PlusIcon,
  ClockIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  SparklesIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  PencilIcon,
  ArrowRightIcon,
  XMarkIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

export default function PoliciesHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full mb-6">
            <DocumentTextIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Understanding Company Policies</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Set up and manage the rules and guidelines for your business operations
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <div className="flex items-start space-x-2 md:space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">What is the Policies Section?</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                This is where you create the rulebook for your business. Define everything from vacation time and sick days to work schedules and pay periods. It's like writing the employee handbook that keeps your team aligned and your business running smoothly.
              </p>
            </div>
          </div>
        </div>

        {/* Step-by-Step Policy Setup Process */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center">Step-by-Step Policy Creation Process</h2>

          <div className="relative">
            {/* Connection Line */}
            <div className="absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-200 via-teal-200 to-emerald-200 hidden md:block"></div>

            <div className="grid md:grid-cols-5 gap-6 md:gap-8">
              {/* Step 1 */}
              <div className="relative">
                <div className="bg-emerald-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-emerald-600 font-bold text-xl">1</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Review Existing</h3>
                  <p className="text-sm text-gray-600">Check current policies in the table</p>
                  <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <EyeIcon className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                    <p className="text-xs text-emerald-700 font-medium">View Policies</p>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <ArrowRightIcon className="w-6 h-6 text-emerald-400" />
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="bg-teal-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-teal-600 font-bold text-xl">2</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Add New Policy</h3>
                  <p className="text-sm text-gray-600">Click Add Policy button</p>
                  <div className="mt-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                    <PlusIcon className="w-6 h-6 text-teal-600 mx-auto mb-1" />
                    <p className="text-xs text-teal-700 font-medium">Add Policy</p>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <ArrowRightIcon className="w-6 h-6 text-teal-400" />
              </div>

              {/* Step 3 */}
              <div className="relative">
                <div className="bg-cyan-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-cyan-600 font-bold text-xl">3</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Configure Rules</h3>
                  <p className="text-sm text-gray-600">Set time off, sick leave, schedules</p>
                  <div className="mt-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                    <PencilIcon className="w-6 h-6 text-cyan-600 mx-auto mb-1" />
                    <p className="text-xs text-cyan-700 font-medium">Configure</p>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <ArrowRightIcon className="w-6 h-6 text-cyan-400" />
              </div>

              {/* Step 4 */}
              <div className="relative">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-blue-600 font-bold text-xl">4</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Save Policy</h3>
                  <p className="text-sm text-gray-600">Review and save your settings</p>
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <CheckCircleIcon className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                    <p className="text-xs text-blue-700 font-medium">Save</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>        {/* Key Policy Areas */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <CalendarIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Time Off & Holidays</h3>
            <p className="text-gray-600 text-sm md:text-base">Set vacation allowances based on days worked or hours. Full-time staff might get 25 days annually, part-time based on hours worked.</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <ClockIcon className="w-6 h-6 text-teal-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Sick Leave</h3>
            <p className="text-gray-600 text-sm md:text-base">Define sick day limits and tracking rules. Helps maintain fair attendance policies and ensures consistent treatment for all employees.</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
              <CalendarIcon className="w-6 h-6 text-cyan-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Work Schedule</h3>
            <p className="text-gray-600 text-sm md:text-base">Configure business operating days and bank holiday policies. Affects time-off requests and payroll calculations.</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <CurrencyDollarIcon className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Pay & Reviews</h3>
            <p className="text-gray-600 text-sm md:text-base">Set payroll schedules and performance review cycles. Keeps your team informed about compensation and evaluation timelines.</p>
          </div>
        </div>

        {/* Getting Started Guide */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
            <SparklesIcon className="w-6 h-6 mr-3 text-emerald-500" />
            How to Create a Policy
          </h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-emerald-600 font-bold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Review Current Policies</h3>
                <p className="text-gray-600">Check the policies table to see what's already configured</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-emerald-600 font-bold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Click Add Policy</h3>
                <p className="text-gray-600">Use the Add Policy button to create new company rules</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-emerald-600 font-bold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Configure Settings</h3>
                <p className="text-gray-600">Set holiday allowances, sick leave limits, and work schedules</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-emerald-600 font-bold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Choose Calculation Method</h3>
                <p className="text-gray-600">Select days or hours for time-off calculations</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-emerald-600 font-bold text-sm">5</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Save & Apply</h3>
                <p className="text-gray-600">Review your settings and save to activate the policy</p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 md:p-8 text-white mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Why Policies Matter</h2>
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Clear Expectations</h3>
                <p className="text-emerald-100">Everyone knows the rules about time off, pay, and schedules</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Fair Treatment</h3>
                <p className="text-emerald-100">Consistent policies ensure equal treatment for all employees</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Legal Compliance</h3>
                <p className="text-emerald-100">Stay compliant with employment laws and regulations</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Better Planning</h3>
                <p className="text-emerald-100">Predictable schedules help with business planning and staffing</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tips & Best Practices */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
            <LightBulbIcon className="w-6 h-6 mr-3 text-emerald-500" />
            Tips for Success
          </h2>
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-600 font-bold text-xs">•</span>
                </div>
                <p className="text-gray-600 text-sm">Start with basic rules like sick days and holidays - you can expand later</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-600 font-bold text-xs">•</span>
                </div>
                <p className="text-gray-600 text-sm">Check local employment laws for minimum holiday entitlement</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-600 font-bold text-xs">•</span>
                </div>
                <p className="text-gray-600 text-sm">Consider different needs for full-time vs part-time employees</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-600 font-bold text-xs">•</span>
                </div>
                <p className="text-gray-600 text-sm">Review policies regularly as your business grows</p>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-start space-x-3">
              <DocumentTextIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800">Need Expert Help?</h3>
                <p className="text-amber-700 text-sm">
                  For complex policy decisions, consider consulting an HR professional or checking local employment laws. This tool helps organize your policies, but expert advice ensures compliance and fairness.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}